import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ShieldCheck, Download, Terminal, RotateCw, Loader2, CheckCircle2, XCircle, Copy, Check } from "lucide-react";

// Self-hosted Microsoft Graph onboarding for the customer's OWN single-tenant app registration.
// Removes the manual Azure-portal work: generate the app certificate on the backend (private key stays
// server-side), download the public .cer, copy a Microsoft Graph PowerShell script that provisions the
// app, and grant admin consent in one click. Reused in the Setup Wizard and Settings.
export function SelfHostedGraphSetup({ onConnected, onStatus, wizard }) {
  const { t } = useLang();
  const [tenantId, setTenantId] = useState("");
  const [clientId, setClientId] = useState("");
  const [cert, setCert] = useState(null); // {thumbprint, notAfter}
  const [graph, setGraph] = useState(null); // {ok, missing:[]}
  const [busy, setBusy] = useState("");
  const [script, setScript] = useState(null);
  const [scriptCopied, setScriptCopied] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      const e = data?.entra || {};
      setTenantId(e.tenantId || ""); setClientId(e.clientId || "");
      if (e.certThumbprint) setCert({ thumbprint: e.certThumbprint, notAfter: e.certNotAfter });
    }).catch(() => {});
    recheck(true);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveIds = async () => {
    setBusy("save");
    try {
      await api.put("/settings", { entra: { tenantId: tenantId.trim(), clientId: clientId.trim() } });
      toast.success(t("shg.idsSaved"));
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(""); }
  };

  const generateCert = async () => {
    setBusy("cert");
    try {
      const { data } = await api.post("/settings/entra/generate-cert");
      setCert({ thumbprint: data.thumbprint, notAfter: data.notAfter });
      toast.success(t("shg.certGenerated"));
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(""); }
  };

  const downloadCert = async () => {
    try {
      const res = await api.get("/settings/entra/public-cert", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "scalio-nexus.cer"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const copyScript = async () => {
    setBusy("script");
    try {
      let text = script;
      if (!text) {
        const { data } = await api.get("/settings/entra/bootstrap-script");
        text = data.script;
        setScript(text);
      }
      await navigator.clipboard.writeText(text || "");
      setScriptCopied(true);
      toast.success(t("shg.copied"));
      setTimeout(() => setScriptCopied(false), 1600);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(""); }
  };

  const downloadScript = async () => {
    setBusy("download");
    try {
      let text = script;
      if (!text) {
        const { data } = await api.get("/settings/entra/bootstrap-script");
        text = data.script;
        setScript(text);
      }
      const blob = new Blob([text || ""], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "scalio-nexus-setup.ps1"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(""); }
  };

  const grantConsent = async () => {
    setBusy("consent");
    try {
      const { data } = await api.get(`/auth/self/admin-consent${wizard ? "?origin=wizard" : ""}`);
      if (data?.url) window.location.href = data.url;
    } catch (e) { toast.error(apiErr(e)); setBusy(""); }
  };

  const recheck = async (silent) => {
    if (!silent) setBusy("recheck");
    try {
      const { data } = await api.post("/settings/test-graph");
      const ok = !!data?.ok;
      const missing = data?.missingRequired || [];
      setGraph((prev) => {
        if (ok && prev && !prev.ok) toast.success(t("shg.consentOk"));
        return { ok, missing };
      });
      onStatus && onStatus({ ok, missing });
      if (ok) { onConnected && onConnected(); clearInterval(pollRef.current); window.dispatchEvent(new Event("cloud-sync-recheck")); }
    } catch { setGraph({ ok: false, missing: [] }); onStatus && onStatus({ ok: false, missing: [] }); }
    finally { if (!silent) setBusy(""); }
  };

  // Auto-poll for consent completion until Graph works.
  useEffect(() => {
    if (graph?.ok) return;
    pollRef.current = setInterval(() => recheck(true), 15000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph?.ok]);

  return (
    <div className="space-y-4" data-testid="selfhosted-graph-setup">
      <p className="text-xs text-zinc-500">{t("shg.intro")}</p>

      {/* Step 1 — App registration IDs */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("shg.tenantId")}</Label>
          <Input value={tenantId} onChange={(e) => setTenantId(e.target.value)} data-testid="shg-tenant" className="rounded-sm border-zinc-300 font-mono text-sm" placeholder="contoso.onmicrosoft.com" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("shg.clientId")}</Label>
          <Input value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="shg-client" className="rounded-sm border-zinc-300 font-mono text-sm" placeholder="00000000-0000-0000-0000-000000000000" />
        </div>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={saveIds} disabled={busy === "save"} data-testid="shg-save-ids" className="rounded-sm border-zinc-300">
        {busy === "save" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}{t("shg.saveIds")}
      </Button>

      {/* Step 2 — Certificate */}
      <div className="rounded-lg border border-zinc-200 bg-zinc-50/70 p-3 space-y-2" data-testid="shg-cert-card">
        <div className="flex items-center gap-1.5 text-sm font-bold text-zinc-800"><KeyRound size={15} /> {t("shg.certTitle")}</div>
        <p className="text-xs text-zinc-500">{t("shg.certDesc")}</p>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" size="sm" onClick={generateCert} disabled={busy === "cert"} data-testid="shg-generate-cert" className="rounded-sm">
            {busy === "cert" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <KeyRound size={14} className="mr-1.5" />}{cert ? t("shg.regenCert") : t("shg.genCert")}
          </Button>
          {cert && (
            <Button type="button" variant="outline" size="sm" onClick={downloadCert} data-testid="shg-download-cert" className="rounded-sm border-zinc-300">
              <Download size={14} className="mr-1.5" /> {t("shg.downloadCer")}
            </Button>
          )}
        </div>
        {cert && (
          <div className="text-[11px] text-zinc-500" data-testid="shg-cert-info">
            <span className="font-semibold">{t("shg.thumbprint")}:</span> <code className="font-mono">{cert.thumbprint}</code>
            {cert.notAfter && <span className="ml-2">· {t("shg.expires")}: {new Date(cert.notAfter).toLocaleDateString()}</span>}
          </div>
        )}
      </div>

      {/* Step 3 — PowerShell script (copy only; never rendered inline to avoid an overflow scrollbar) */}
      <div className="rounded-lg border border-zinc-200 bg-zinc-50/70 p-3 space-y-2" data-testid="shg-script-card">
        <div className="flex items-center gap-1.5 text-sm font-bold text-zinc-800"><Terminal size={15} /> {t("shg.scriptTitle")}</div>
        <p className="text-xs text-zinc-500">{t("shg.scriptDesc")}</p>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={copyScript} disabled={busy === "script" || !cert} data-testid="shg-copy-script" className="rounded-sm border-zinc-300">
            {busy === "script" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : scriptCopied ? <Check size={14} className="mr-1.5 text-emerald-600" /> : <Copy size={14} className="mr-1.5" />} {scriptCopied ? t("shg.copied") : t("shg.copyScript")}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={downloadScript} disabled={busy === "download" || !cert} data-testid="shg-download-script" className="rounded-sm border-zinc-300">
            {busy === "download" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />} {t("shg.downloadScript")}
          </Button>
        </div>
        {!cert && <p className="text-[11px] text-amber-600">{t("shg.scriptNeedCert")}</p>}
      </div>

      {/* Step 4 — Admin consent + status */}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" onClick={grantConsent} disabled={busy === "consent" || !tenantId || !clientId} data-testid="shg-grant-consent" className="rounded-sm bg-primary hover:bg-[#002480]">
          {busy === "consent" ? <Loader2 size={15} className="mr-2 animate-spin" /> : <ShieldCheck size={15} className="mr-2" />} {t("shg.grantConsent")}
        </Button>
        <Button type="button" variant="outline" onClick={() => recheck(false)} disabled={busy === "recheck"} data-testid="shg-recheck" className="rounded-sm border-zinc-300">
          {busy === "recheck" ? <Loader2 size={15} className="mr-2 animate-spin" /> : <RotateCw size={15} className="mr-2" />} {t("shg.recheck")}
        </Button>
        {graph?.ok
          ? <span className="inline-flex items-center gap-1 rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700" data-testid="shg-status-ok"><CheckCircle2 size={14} /> {t("shg.connected")}</span>
          : <span className="inline-flex items-center gap-1 rounded-sm border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-bold text-amber-700" data-testid="shg-status-missing"><XCircle size={14} /> {t("shg.notConnected")}</span>}
      </div>
      {graph && !graph.ok && graph.missing?.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="shg-missing-perms">
          {graph.missing.map((p) => (
            <span key={p} className="inline-flex items-center rounded-sm bg-red-100 px-2 py-0.5 font-mono text-[11px] font-medium text-red-800">{p}</span>
          ))}
        </div>
      )}
    </div>
  );
}
